import { Edit, Delete, KeyboardArrowDown, KeyboardArrowRight } from '@mui/icons-material';
import { IconButton, Box, Chip, Typography, Stack, CircularProgress } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridPaginationModel,
  GridSortModel,
  GridRowsProp,
  GridRowHeightParams,
} from '@mui/x-data-grid';
import { memo, useMemo, useCallback, useState } from 'react';
import { formatUTCForDisplay } from '../../utils/dateUtils';
import type { StandardizedVoyageWithVessel, EUETSLegPart } from '@/types';

// Row types for the DataGrid
type VoyageRow = StandardizedVoyageWithVessel & {
  id: string;
  rowType: 'voyage';
  effectiveCO2: number;
  isEdited: boolean;
};

type LegRow = {
  id: string;
  rowType: 'leg';
  parentVoyageId: string;
  leg: EUETSLegPart;
  portDepartureName: string;
  portArrivalName: string;
  atd: string;
  ata: string;
  effectiveCO2: number;
  euetsScope: string;
  legMode: string;
};

type LoadingRow = {
  id: string;
  rowType: 'loading';
  voyageID: string;
};

type NoLegsRow = {
  id: string;
  rowType: 'no-legs';
  voyageID: string;
  message: string;
};

type TableRow = VoyageRow | LegRow | LoadingRow | NoLegsRow;

// Preprocessing function for setting editedco2 or originalco2
const preprocessVoyageData = (voyages: StandardizedVoyageWithVessel[]) => {
  return voyages.map((voyage) => ({
    ...voyage,
    effectiveCO2:
      voyage.editedEUEmissionsCO2 && voyage.editedEUEmissionsCO2 !== voyage.euEmissionsCO2
        ? voyage.editedEUEmissionsCO2
        : voyage.euEmissionsCO2,
    isEdited: voyage.editedEUEmissionsCO2 && voyage.editedEUEmissionsCO2 !== voyage.euEmissionsCO2,
  }));
};

interface StandardizedVoyagesDataGridProps {
  voyages: StandardizedVoyageWithVessel[];
  loading?: boolean;
  onEdit?: (voyage: StandardizedVoyageWithVessel) => void;
  onDelete?: (voyage: StandardizedVoyageWithVessel, event?: React.MouseEvent<HTMLElement>) => void;
  totalCount?: number;
  page?: number;
  pageSize?: number;
  onPaginationModelChange?: (model: GridPaginationModel) => void;
  onSortModelChange?: (model: GridSortModel) => void;
}

export const StandardizedVoyagesDataGrid = memo(
  ({
    voyages,
    loading = false,
    onEdit,
    onDelete,
    totalCount = 0,
    page = 0,
    pageSize = 25,
    onPaginationModelChange,
    onSortModelChange,
  }: StandardizedVoyagesDataGridProps) => {
    const theme = useTheme();

    // State for expanded voyage rows (showing legs)
    const [expandedVoyageIds, setExpandedVoyageIds] = useState<Set<string>>(new Set());
    // State to cache loaded legs for each voyage
    const [loadedLegs, setLoadedLegs] = useState<Map<string, EUETSLegPart[]>>(new Map());
    // State to track which voyages are currently loading legs
    const [loadingLegIds, setLoadingLegIds] = useState<Set<string>>(new Set());

    // Toggle voyage expansion to show/hide legs
    const handleToggleExpand = useCallback(
      async (voyageId: string, vesselId: string, hasLegIds: boolean) => {
        setExpandedVoyageIds((prev) => {
          const next = new Set(prev);
          if (next.has(voyageId)) {
            // Collapse - just remove from expanded set
            next.delete(voyageId);
          } else {
            // Expand - add to expanded set
            next.add(voyageId);

            // Fetch legs if we haven't loaded them yet and voyage has leg IDs
            if (hasLegIds && !loadedLegs.has(voyageId) && !loadingLegIds.has(voyageId)) {
              setLoadingLegIds((prev) => new Set(prev).add(voyageId));

              // Fetch legs from API
              import('../../services/voyageService').then(({ VoyageService }) => {
                VoyageService.fetchVoyageLegs(vesselId, voyageId)
                  .then((legs) => {
                    setLoadedLegs((prev) => new Map(prev).set(voyageId, legs || []));
                    setLoadingLegIds((prev) => {
                      const next = new Set(prev);
                      next.delete(voyageId);
                      return next;
                    });
                  })
                  .catch((error) => {
                    console.error('Failed to fetch legs for voyage', voyageId, error);
                    setLoadedLegs((prev) => new Map(prev).set(voyageId, []));
                    setLoadingLegIds((prev) => {
                      const next = new Set(prev);
                      next.delete(voyageId);
                      return next;
                    });
                  });
              });
            }
          }
          return next;
        });
      },
      [loadedLegs, loadingLegIds]
    );

    // Preprocess voyages for setting editedco2 or originalco2
    const processedVoyages = useMemo(() => preprocessVoyageData(voyages), [voyages]);

    const handleEdit = useCallback(
      (voyage: StandardizedVoyageWithVessel) => {
        onEdit?.(voyage);
      },
      [onEdit]
    );

    const handleDelete = useCallback(
      (voyage: StandardizedVoyageWithVessel, event?: React.MouseEvent<HTMLElement>) => {
        onDelete?.(voyage, event);
      },
      [onDelete]
    );

    // Dynamic row height for leg rows
    const getRowHeight = useCallback((params: GridRowHeightParams) => {
      const rowType = (params.model as TableRow)?.rowType;
      if (rowType === 'leg' || rowType === 'loading' || rowType === 'no-legs') {
        return 'auto';
      }
      return 52; // Default row height for voyage rows
    }, []);

    // Create table rows with interleaved leg rows when expanded
    const tableRows: GridRowsProp = useMemo(() => {
      const rows: TableRow[] = [];

      processedVoyages?.forEach((voyage: StandardizedVoyageWithVessel, index: number) => {
        // Add the voyage row
        rows.push({
          id: voyage?.voyageID || `voyage-${index}`,
          rowType: 'voyage',
          ...voyage,
        } as VoyageRow);

        // Add leg rows if voyage is expanded
        if (expandedVoyageIds.has(voyage?.voyageID)) {
          const isLoading = loadingLegIds.has(voyage?.voyageID);
          const cachedLegs = loadedLegs.get(voyage?.voyageID);

          if (isLoading) {
            // Show loading placeholder
            rows.push({
              id: `${voyage?.voyageID}-loading`,
              rowType: 'loading',
              voyageID: voyage?.voyageID,
            });
          } else if (cachedLegs && cachedLegs.length > 0) {
            // Show loaded leg rows
            cachedLegs.forEach((leg: EUETSLegPart, legIndex: number) => {
              rows.push({
                id: `${voyage?.voyageID}-leg-${leg.legPartId || legIndex}`,
                rowType: 'leg',
                parentVoyageId: voyage?.voyageID,
                leg: leg,
                portDepartureName: leg.portFromName,
                portArrivalName: leg.portToName,
                atd: leg.legStartDateTime,
                ata: leg.legEndDateTime,
                effectiveCO2: leg.co2EmittedMtEUETS,
                euetsScope: leg.euetsScope,
                legMode: leg.legMode,
              });
            });
          } else if (cachedLegs && cachedLegs.length === 0) {
            // Loaded but no legs found
            rows.push({
              id: `${voyage?.voyageID}-no-legs`,
              rowType: 'no-legs',
              voyageID: voyage?.voyageID,
              message: 'No legs available for this voyage',
            });
          }
        }
      });

      return rows;
    }, [processedVoyages, expandedVoyageIds, loadedLegs, loadingLegIds]);

    const columns: GridColDef[] = useMemo(
      () => [
        {
          field: 'expand',
          headerName: '',
          width: 50,
          sortable: false,
          filterable: false,
          disableColumnMenu: true,
          renderCell: (params: GridRenderCellParams<TableRow>) => {
            const rowType = params.row?.rowType;

            // For non-voyage rows, show indent indicator for leg rows
            if (rowType === 'leg') {
              return <Box sx={{ width: 24, height: 24, ml: 1 }}>↳</Box>;
            }
            if (rowType === 'loading' || rowType === 'no-legs') {
              return <Box sx={{ width: 24, height: 24, ml: 1 }} />;
            }

            const voyage = params.row;
            const hasLegs = voyage?.euEtsLegPartIds && voyage.euEtsLegPartIds.length > 0;
            const isExpanded = expandedVoyageIds.has(voyage?.voyageID);

            if (!hasLegs) {
              return <Box sx={{ width: 24, height: 24 }} />;
            }

            return (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleToggleExpand(voyage.voyageID, voyage.vesselID, hasLegs);
                }}
                sx={{ p: 0.25 }}
              >
                {isExpanded ? (
                  <KeyboardArrowDown fontSize="small" />
                ) : (
                  <KeyboardArrowRight fontSize="small" />
                )}
              </IconButton>
            );
          },
        },
        {
          field: 'voyageID',
          headerName: 'Voyage ID',
          width: 150,
          flex: 1,
          renderCell: (params: GridRenderCellParams<TableRow>) => {
            const rowType = params.row?.rowType;
            if (rowType === 'loading' || rowType === 'no-legs' || rowType === 'leg') {
              return <></>;
            }
            return <span>{params.value}</span>;
          },
        },
        {
          field: 'vesselName',
          headerName: 'Vessel Name',
          width: 200,
          flex: 1,
          sortable: false,
          disableColumnMenu: true,
          renderCell: (params: GridRenderCellParams<TableRow>) => {
            const rowType = params.row?.rowType;
            if (rowType === 'loading' || rowType === 'no-legs' || rowType === 'leg') {
              return <></>;
            }
            return <span>{params.value}</span>;
          },
        },
        {
          field: 'voyageNumber',
          headerName: 'Voyage #',
          width: 100,
          type: 'number',
          sortable: true,
          renderCell: (params: GridRenderCellParams<TableRow>) => {
            const rowType = params.row?.rowType;
            if (rowType === 'loading' || rowType === 'no-legs' || rowType === 'leg') {
              return <></>;
            }
            return <span>{params.value}</span>;
          },
        },
        {
          field: 'imo',
          headerName: 'IMO',
          width: 120,
          sortable: false,
          disableColumnMenu: true,
          renderCell: (params: GridRenderCellParams<TableRow>) => {
            const rowType = params.row?.rowType;
            if (rowType === 'loading' || rowType === 'no-legs' || rowType === 'leg') {
              return <></>;
            }
            return <span>{params.value}</span>;
          },
        },
        {
          field: 'portDepartureName',
          headerName: 'Departure Port',
          width: 180,
          flex: 1,
          sortable: false,
          disableColumnMenu: true,
          renderCell: (params: GridRenderCellParams<TableRow>) => {
            const rowType = params.row?.rowType;

            // Loading row
            if (rowType === 'loading') {
              return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 2 }}>
                  <CircularProgress size={14} />
                  <Typography variant="body2" color="text.secondary">
                    Loading legs...
                  </Typography>
                </Box>
              );
            }

            // No legs row
            if (rowType === 'no-legs') {
              return (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ ml: 2, fontStyle: 'italic' }}
                >
                  {params.row?.message || 'No leg data available for this voyage.'}
                </Typography>
              );
            }

            // Leg row
            if (rowType === 'leg') {
              const leg = params.row?.leg as EUETSLegPart;
              return (
                <Box sx={{ ml: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {params.row?.portDepartureName} → {params.row?.portArrivalName}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                    {leg?.euetsScope && (
                      <Chip
                        label={leg.euetsScope}
                        size="small"
                        color={
                          leg.euetsScope === 'Within EU'
                            ? 'success'
                            : leg.euetsScope === 'Into the EU' || leg.euetsScope === 'Out of the EU'
                              ? 'warning'
                              : 'default'
                        }
                        sx={{ fontSize: '0.6rem', height: 16 }}
                      />
                    )}
                    {leg?.legMode && (
                      <Chip
                        label={leg.legMode}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '0.6rem', height: 16 }}
                      />
                    )}
                  </Box>
                </Box>
              );
            }

            // Voyage row
            const voyage = params.row;
            const hasLegIds = voyage?.euEtsLegPartIds && voyage.euEtsLegPartIds.length > 0;
            const cachedLegs = loadedLegs.get(voyage?.voyageID);
            const legCount = cachedLegs?.length || voyage?.euEtsLegPartIds?.length || 0;

            return (
              <Box>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="body2">{voyage?.portDepartureName || '-'}</Typography>
                  {hasLegIds && (
                    <Chip
                      label={`${legCount} leg${legCount !== 1 ? 's' : ''}`}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: '0.6rem', height: 16 }}
                    />
                  )}
                </Stack>
              </Box>
            );
          },
        },
        {
          field: 'portArrivalName',
          headerName: 'Arrival Port',
          width: 150,
          flex: 1,
          sortable: false,
          disableColumnMenu: true,
          renderCell: (params: GridRenderCellParams<TableRow>) => {
            const rowType = params.row?.rowType;
            if (rowType === 'loading' || rowType === 'no-legs') {
              return <></>;
            }
            return <span>{params.value}</span>;
          },
        },
        {
          field: 'atd',
          headerName: 'Departure Date',
          width: 200,
          type: 'dateTime',
          sortable: true,
          valueGetter: (params) => (params ? new Date(params) : null),
          valueFormatter: (value: Date) =>
            value ? formatUTCForDisplay(value.toISOString(), false) : '',
          renderCell: (params: GridRenderCellParams<TableRow>) => {
            const rowType = params.row?.rowType;
            if (rowType === 'loading' || rowType === 'no-legs') {
              return <></>;
            }
            const value = params.value;
            return <span>{value ? formatUTCForDisplay(value.toISOString(), false) : ''}</span>;
          },
        },
        {
          field: 'ata',
          headerName: 'Arrival Date',
          width: 200,
          type: 'dateTime',
          sortable: true,
          valueGetter: (params) => (params ? new Date(params) : null),
          valueFormatter: (value: Date) =>
            value ? formatUTCForDisplay(value.toISOString(), false) : '',
          renderCell: (params: GridRenderCellParams<TableRow>) => {
            const rowType = params.row?.rowType;
            if (rowType === 'loading' || rowType === 'no-legs') {
              return <></>;
            }
            const value = params.value;
            return <span>{value ? formatUTCForDisplay(value.toISOString(), false) : ''}</span>;
          },
        },
        {
          field: 'effectiveCO2',
          headerName: 'CO2 Emissions (tons)',
          width: 180,
          type: 'number',
          sortable: true,
          valueFormatter: (value: number) => value?.toFixed(2) || '0.00',
          renderCell: (params: GridRenderCellParams<TableRow>) => {
            const rowType = params.row?.rowType;

            // For loading/no-legs rows, show nothing
            if (rowType === 'loading' || rowType === 'no-legs') {
              return <></>;
            }

            // For leg rows, show leg emissions
            if (rowType === 'leg') {
              return (
                <Typography sx={{ fontSize: '14px', pt: 0.5 }}>
                  {params.row?.effectiveCO2?.toFixed(2) || '0.00'}
                </Typography>
              );
            }

            // Voyage row
            const isEdited = params.row.isEdited;
            const value = params.row.effectiveCO2;

            return (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <span
                  style={{
                    color: isEdited ? '#2e7d32' : 'inherit',
                    fontWeight: isEdited ? 500 : 'normal',
                  }}
                >
                  {value?.toFixed(2) || '0.00'}
                </span>
                {isEdited && (
                  <Chip
                    label="Edited"
                    size="small"
                    color="primary"
                    variant="outlined"
                    sx={{ fontSize: '0.7rem', height: 16 }}
                  />
                )}
              </Box>
            );
          },
        },
        {
          field: 'dataType',
          headerName: 'Data Type',
          width: 120,
          sortable: false,
          renderCell: (params: GridRenderCellParams<TableRow>) => {
            const rowType = params.row?.rowType;
            if (rowType === 'loading' || rowType === 'no-legs' || rowType === 'leg') {
              return <></>;
            }
            return (
              <Chip
                label={params.value === 'actual' ? 'Actual' : 'Estimate'}
                color={params.value === 'actual' ? 'success' : 'warning'}
                size="small"
                variant="outlined"
                sx={{
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  height: 24,
                }}
              />
            );
          },
        },
        {
          field: 'vesselType',
          headerName: 'Vessel Type',
          width: 120,
          sortable: false,
          renderCell: (params: GridRenderCellParams<TableRow>) => {
            const rowType = params.row?.rowType;
            if (rowType === 'loading' || rowType === 'no-legs' || rowType === 'leg') {
              return <></>;
            }
            return <span>{params.value}</span>;
          },
        },
        {
          field: 'dataSource',
          headerName: 'Data Source',
          width: 120,
          sortable: false,
          renderCell: (params: GridRenderCellParams<TableRow>) => {
            const rowType = params.row?.rowType;
            if (rowType === 'loading' || rowType === 'no-legs' || rowType === 'leg') {
              return <></>;
            }
            return <span>{params.value}</span>;
          },
        },
        {
          field: 'voyageCharterer',
          headerName: 'Charterer',
          width: 150,
          sortable: false,
          disableColumnMenu: true,
          renderCell: (params: GridRenderCellParams<TableRow>) => {
            const rowType = params.row?.rowType;
            if (rowType === 'loading' || rowType === 'no-legs' || rowType === 'leg') {
              return <></>;
            }
            return <span>{params.value}</span>;
          },
        },
        {
          field: 'actions',
          headerName: 'Actions',
          width: 120,
          sortable: false,
          filterable: false,
          renderCell: (params: GridRenderCellParams<TableRow>) => {
            const rowType = params.row?.rowType;
            if (rowType === 'loading' || rowType === 'no-legs' || rowType === 'leg') {
              return <></>;
            }
            return (
              <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', height: '100%' }}>
                {onEdit && (
                  <IconButton
                    size="small"
                    onClick={() => handleEdit(params.row as VoyageRow)}
                    color="primary"
                  >
                    <Edit fontSize="small" />
                  </IconButton>
                )}
                {onDelete && (
                  <IconButton
                    size="small"
                    onClick={(e) => handleDelete(params.row as VoyageRow, e)}
                    color="error"
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                )}
              </Box>
            );
          },
        },
      ],
      [
        onEdit,
        onDelete,
        handleEdit,
        handleDelete,
        expandedVoyageIds,
        handleToggleExpand,
        loadedLegs,
      ]
    );

    const paginationModelMemo = useMemo(() => ({ page, pageSize }), [page, pageSize]);

    return (
      <Box sx={{ width: '100%' }}>
        <DataGrid
          rows={tableRows}
          columns={columns}
          getRowId={(row) => row.id}
          getRowHeight={getRowHeight}
          getRowClassName={(params) => {
            const rowType = params.row?.rowType;
            if (rowType === 'leg') return 'leg-row';
            if (rowType === 'loading') return 'loading-row';
            if (rowType === 'no-legs') return 'no-legs-row';
            return 'voyage-row';
          }}
          loading={loading}
          paginationMode="server"
          rowCount={totalCount}
          paginationModel={paginationModelMemo}
          onPaginationModelChange={onPaginationModelChange}
          pageSizeOptions={[10, 25, 50, 100]}
          sortingMode="server"
          onSortModelChange={onSortModelChange}
          disableRowSelectionOnClick
          autoHeight={false}
          sx={{
            height: 600,
            width: '100%',
            '& .MuiDataGrid-cell': {
              alignItems: 'center',
              display: 'flex',
            },
            '& .voyage-row .MuiDataGrid-cell': {
              alignItems: 'center',
            },
            '& .leg-row': {
              backgroundColor:
                theme.palette.mode === 'dark'
                  ? 'rgba(144, 202, 249, 0.08)'
                  : 'rgba(100, 181, 246, 0.08)',
              '&:hover': {
                backgroundColor:
                  theme.palette.mode === 'dark'
                    ? 'rgba(144, 202, 249, 0.12)'
                    : 'rgba(100, 181, 246, 0.12)',
              },
            },
            '& .loading-row': {
              backgroundColor: 'transparent',
              '&:hover': {
                backgroundColor: 'transparent',
              },
            },
            '& .no-legs-row': {
              backgroundColor: 'transparent',
              '&:hover': {
                backgroundColor: 'transparent',
              },
            },
          }}
        />
      </Box>
    );
  }
);

StandardizedVoyagesDataGrid.displayName = 'StandardizedVoyagesDataGrid';
